import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Calculator, 
  TrendingUp, 
  PiggyBank, 
  Home, 
  Euro, 
  CheckCircle2,
  ArrowRight,
  Percent,
  AlertTriangle,
  Lightbulb
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function CalculRentabiliteEmprunt() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Helmet>
        <title>Calcul Rentabilité Locative avec Emprunt | Guide Complet 2025</title>
        <meta name="description" content="Comment calculer la rentabilité locative avec un emprunt bancaire ? Découvrez la méthode complète : effet de levier, cash-flow, mensualités et taux d'endettement." />
        <meta name="keywords" content="calcul rentabilité locative avec emprunt, rentabilité investissement immobilier crédit, effet de levier immobilier, cash-flow avec emprunt" />
        <link rel="canonical" href="https://achat-immo-gagnant.fr/guides/calcul-rentabilite-locative-emprunt" />
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Home className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-800">Achat Immo Gagnant</span>
          </Link>
          <Link to="/">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              Simuler avec emprunt
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Calcul de Rentabilité Locative avec Emprunt
          </h1>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            L'emprunt bancaire est le secret des investisseurs rentables. 
            Découvrez comment l'intégrer dans votre calcul de rentabilité.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Introduction */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">
            Pourquoi emprunter pour investir dans l'immobilier ?
          </h2>
          <div className="prose prose-lg text-slate-600">
            <p>
              Contrairement aux idées reçues, <strong>emprunter pour investir dans l'immobilier</strong> est souvent plus rentable que d'acheter comptant. C'est ce qu'on appelle l'<strong>effet de levier</strong>.
            </p>
            <p>
              L'idée est simple : vous utilisez l'argent de la banque pour acquérir un bien, et ce sont les loyers de vos locataires qui remboursent le crédit. À la fin du prêt, vous possédez un bien immobilier que vous n'avez (presque) pas payé vous-même.
            </p>
          </div>
        </section>

        {/* Effet de levier */}
        <section className="mb-16 bg-emerald-50 rounded-2xl p-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">
            L'effet de levier expliqué
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-6 border border-emerald-200">
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Achat comptant (100 000€)</h3>
              <ul className="space-y-2 text-slate-600">
                <li>• Investissement : <strong>100 000€</strong></li>
                <li>• Loyer annuel : 6 000€</li>
                <li>• Rentabilité : <strong>6%</strong></li>
                <li>• Argent immobilisé : 100 000€</li>
              </ul>
              <div className="mt-4 p-3 bg-slate-100 rounded-lg">
                <p className="text-sm text-slate-600">Vous n'avez qu'un seul bien et votre capital est bloqué.</p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-emerald-400 ring-2 ring-emerald-400">
              <h3 className="text-xl font-semibold text-emerald-700 mb-4">Achat avec emprunt (10 000€ apport)</h3>
              <ul className="space-y-2 text-slate-600">
                <li>• Apport : <strong>10 000€</strong></li>
                <li>• Emprunt : 90 000€</li>
                <li>• Loyer annuel : 6 000€</li>
                <li>• Mensualité crédit : 450€/mois</li>
                <li>• Rentabilité sur apport : <strong>15%+</strong></li>
              </ul>
              <div className="mt-4 p-3 bg-emerald-100 rounded-lg">
                <p className="text-sm text-emerald-700 font-medium">Vous pourriez acheter 10 biens avec le même capital !</p>
              </div>
            </div>
          </div>
        </section>

        {/* Formule de calcul */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">
            La formule du cash-flow avec emprunt
          </h2>
          
          <div className="bg-slate-900 text-white rounded-xl p-8 mb-6">
            <p className="text-lg font-mono text-center">
              Cash-flow = Loyers - Charges - Mensualité crédit
            </p>
          </div>
          
          <div className="prose prose-lg text-slate-600">
            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">Exemple concret</h3>
            <p>Prenons un appartement T2 de 100 000€ à Bordeaux :</p>
            
            <div className="bg-white rounded-xl p-6 border border-slate-200 my-6">
              <h4 className="font-semibold text-slate-800 mb-4">Données de l'investissement</h4>
              <ul className="space-y-2">
                <li>• Prix d'achat : 100 000€</li>
                <li>• Frais de notaire : 8 000€</li>
                <li>• Emprunt : 108 000€ (prêt 110%)</li>
                <li>• Durée : 20 ans à 3,5%</li>
                <li>• Mensualité : <strong>623€/mois</strong></li>
              </ul>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-slate-200 my-6">
              <h4 className="font-semibold text-slate-800 mb-4">Revenus et charges</h4>
              <ul className="space-y-2">
                <li>• Loyer mensuel : 700€</li>
                <li>• Taxe foncière : 100€/mois (1 200€/an)</li>
                <li>• Assurance PNO : 15€/mois</li>
                <li>• Provision travaux : 50€/mois</li>
                <li>• <strong>Total charges : 165€/mois</strong></li>
              </ul>
            </div>
            
            <div className="bg-emerald-50 rounded-xl p-6 border-2 border-emerald-400 my-6">
              <h4 className="font-semibold text-emerald-800 mb-4">Calcul du cash-flow</h4>
              <p className="text-lg">
                Cash-flow = 700€ - 165€ - 623€ = <strong className="text-red-600">-88€/mois</strong>
              </p>
              <p className="text-sm text-slate-600 mt-4">
                Dans cet exemple, le cash-flow est négatif : vous devez sortir 88€ de votre poche chaque mois. 
                Ce n'est pas forcément un mauvais investissement si la plus-value attendue compense.
              </p>
            </div>
          </div>
        </section>

        {/* Optimiser le cash-flow */}
        <section className="mb-16 bg-amber-50 rounded-2xl p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Lightbulb className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800">
                Comment obtenir un cash-flow positif ?
              </h2>
              <p className="text-slate-600">Les leviers pour améliorer la rentabilité de votre investissement</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <h3 className="font-semibold text-slate-800 mb-2">Allonger la durée du prêt</h3>
              <p className="text-sm text-slate-600">
                Passer de 20 à 25 ans réduit la mensualité de ~15%. Impact sur le cash-flow immédiat.
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <h3 className="font-semibold text-slate-800 mb-2">Négocier le taux</h3>
              <p className="text-sm text-slate-600">
                0,5% de moins sur le taux = 30€/mois économisés sur 100 000€ empruntés.
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <h3 className="font-semibold text-slate-800 mb-2">Location meublée / Airbnb</h3>
              <p className="text-sm text-slate-600">
                Les loyers en meublé sont 20-30% plus élevés qu'en nu. Airbnb peut doubler les revenus.
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <h3 className="font-semibold text-slate-800 mb-2">Investir en province</h3>
              <p className="text-sm text-slate-600">
                Les rendements sont souvent meilleurs hors des grandes métropoles.
              </p>
            </div>
          </div>
        </section>

        {/* Taux d'endettement */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">
            Le taux d'endettement : la limite à respecter
          </h2>
          <div className="prose prose-lg text-slate-600">
            <p>
              Depuis 2022, les banques appliquent strictement la règle du <strong>taux d'endettement maximum de 35%</strong> (assurance comprise). Cela signifie que vos mensualités de crédit ne doivent pas dépasser 35% de vos revenus.
            </p>
            
            <div className="bg-red-50 rounded-xl p-6 border border-red-200 my-6">
              <div className="flex items-center gap-3 mb-3">
                <AlertTriangle className="h-6 w-6 text-red-600" />
                <h3 className="font-semibold text-red-800">Attention au calcul différentiel</h3>
              </div>
              <p className="text-slate-700">
                Certaines banques utilisent le <strong>calcul différentiel</strong> qui permet de déduire 70% des loyers des charges avant de calculer l'endettement. C'est plus favorable pour les investisseurs. Renseignez-vous auprès de votre courtier.
              </p>
            </div>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">Exemple de calcul d'endettement</h3>
            <ul>
              <li>Revenus nets mensuels : 3 500€</li>
              <li>Mensualité résidence principale : 800€</li>
              <li>Mensualité investissement locatif : 623€</li>
              <li>Loyer perçu (70%) : -490€</li>
              <li><strong>Endettement = (800 + 623 - 490) / 3 500 = 26,6%</strong></li>
            </ul>
            <p>Dans cet exemple, le projet est finançable car l'endettement reste sous 35%.</p>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-2xl p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            Calculez votre rentabilité avec emprunt
          </h2>
          <p className="text-emerald-100 mb-6 max-w-xl mx-auto">
            Notre simulateur intègre automatiquement le calcul du crédit, des mensualités et du cash-flow.
          </p>
          <Link to="/">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50 text-lg px-8">
              Lancer la simulation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </section>

        {/* Related links */}
        <section className="mt-16">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">Guides connexes</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/guides/simulateur-rentabilite-locative" className="bg-white rounded-lg p-4 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-semibold text-blue-600">Simulateur rentabilité locative</h3>
              <p className="text-sm text-slate-600">Le guide complet pour calculer votre rentabilité</p>
            </Link>
            <Link to="/guides/cash-flow-immobilier" className="bg-white rounded-lg p-4 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-semibold text-blue-600">Cash-flow immobilier</h3>
              <p className="text-sm text-slate-600">L'indicateur clé de l'autofinancement</p>
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-16">
        <div className="max-w-6xl mx-auto px-4 text-center text-slate-500 text-sm">
          © 2025 Achat Immo Gagnant. Tous droits réservés.
        </div>
      </footer>
    </div>
  );
}
